/**
 * InvoiceDetailsDrawer — premium right-side drawer for a single invoice.
 * Sections: Invoice Summary · Financial Summary · Payment History · Timeline ·
 * Documents · Audit. All data is pulled from `/api/invoices/{iid}/details` in
 * ONE round-trip so the drawer opens instantly.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";
import { toast } from "sonner";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import {
  Banknote, Smartphone, CreditCard, Building2, FileText, Wallet, Receipt,
  Download, Printer, Mail, MessageCircle, Loader2, Sparkles, Clock, User,
  ArrowUpRight, X as XIcon,
} from "lucide-react";

const INR = (n) => `₹${Number(n || 0).toLocaleString("en-IN", {
  minimumFractionDigits: 0, maximumFractionDigits: 2,
})}`;
const fmtDT = (iso) => iso ? new Date(iso).toLocaleString("en-IN", {
  day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
}) : "—";
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-IN", {
  day: "numeric", month: "short", year: "numeric",
}) : "—";

const METHOD_META = {
  cash:          { label: "Cash",          icon: Banknote,   tone: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  upi:           { label: "UPI",           icon: Smartphone, tone: "text-violet-700 bg-violet-50 border-violet-200" },
  credit_card:   { label: "Credit card",   icon: CreditCard, tone: "text-blue-700 bg-blue-50 border-blue-200" },
  debit_card:    { label: "Debit card",    icon: CreditCard, tone: "text-blue-700 bg-blue-50 border-blue-200" },
  bank_transfer: { label: "Bank transfer", icon: Building2,  tone: "text-slate-700 bg-slate-50 border-slate-200" },
  neft:          { label: "NEFT",          icon: Building2,  tone: "text-slate-700 bg-slate-50 border-slate-200" },
  rtgs:          { label: "RTGS",          icon: Building2,  tone: "text-slate-700 bg-slate-50 border-slate-200" },
  imps:          { label: "IMPS",          icon: Building2,  tone: "text-slate-700 bg-slate-50 border-slate-200" },
  cheque:        { label: "Cheque",        icon: FileText,   tone: "text-amber-700 bg-amber-50 border-amber-200" },
  wallet:        { label: "Wallet",        icon: Wallet,     tone: "text-purple-700 bg-purple-50 border-purple-200" },
  razorpay:      { label: "Razorpay",      icon: Sparkles,   tone: "text-indigo-700 bg-indigo-50 border-indigo-200" },
  stripe:        { label: "Stripe",        icon: Sparkles,   tone: "text-indigo-700 bg-indigo-50 border-indigo-200" },
};

export default function InvoiceDetailsDrawer({ invoiceId, open, onOpenChange, onCollectPayment }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !invoiceId) return;
    setLoading(true); setData(null);
    api.get(`/invoices/${invoiceId}/details`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e, "Could not load invoice")))
      .finally(() => setLoading(false));
  }, [open, invoiceId]);

  const downloadPdf = async (variant = "digital") => {
    if (!data) return;
    const inv = data.invoice;
    try {
      const resp = await api.get(`/invoices/${inv.id}/pdf?variant=${variant}`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = `invoice-${inv.invoice_number || inv.id}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch { toast.error("PDF download failed"); }
  };
  const emailInvoice = async () => {
    if (!data) return;
    const inv = data.invoice;
    const to = window.prompt("Send invoice to which email?", data.patient?.email || "");
    if (!to) return;
    const tid = toast.loading("Sending email…");
    try {
      const r = await api.post(`/invoices/${inv.id}/email`, { to });
      toast.success(`Emailed to ${r.data.to}`, { id: tid });
    } catch (err) { toast.error(apiError(err, "Failed to send"), { id: tid }); }
  };
  const whatsappInvoice = async () => {
    if (!data) return;
    const inv = data.invoice;
    const tid = toast.loading("Sending on WhatsApp…");
    try {
      await api.post(`/invoices/${inv.id}/whatsapp`);
      toast.success("Sent on WhatsApp", { id: tid });
    } catch (err) { toast.error(apiError(err, "Failed to send"), { id: tid }); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl p-0 overflow-y-auto bg-linen/40"
        data-testid="invoice-details-drawer"
      >
        {loading && (
          <div className="p-8 text-sm text-ink-mid inline-flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading invoice…
          </div>
        )}
        {!loading && data && <DrawerBody data={data}
                                          onDownloadPdf={downloadPdf}
                                          onEmail={emailInvoice}
                                          onWhatsapp={whatsappInvoice}
                                          onCollectPayment={onCollectPayment} />}
      </SheetContent>
    </Sheet>
  );
}


function DrawerBody({ data, onDownloadPdf, onEmail, onWhatsapp, onCollectPayment }) {
  const { invoice, patient, treatments, doctor_names,
          payment_history, timeline, account_summary, audit } = data;
  const outstanding = account_summary.current_outstanding;
  const paid = account_summary.payment_received + (account_summary.wallet_applied || 0);
  const canCollect = ["pending", "partial", "overdue"].includes(invoice.status);

  return (
    <>
      <SheetHeader className="px-6 pt-6 pb-4 bg-white border-b border-[#EFEADC] sticky top-0 z-10">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="overline text-ink-soft">Invoice</div>
            <SheetTitle className="font-display text-2xl text-ink tracking-tight truncate">
              {invoice.invoice_number}
            </SheetTitle>
            <div className="text-xs text-ink-mid mt-1">{fmtDate(invoice.created_at)}</div>
          </div>
          <StatusBadge status={invoice.status} />
        </div>
      </SheetHeader>

      {/* Sticky action bar */}
      <div className="px-6 py-3 bg-white border-b border-[#F0EBE0] flex items-center gap-2 flex-wrap">
        {canCollect && (
          <button
            onClick={() => onCollectPayment?.(patient)}
            data-testid="drawer-collect-payment"
            className="btn-primary text-xs inline-flex items-center gap-1.5 py-2"
          >
            <Banknote className="w-3.5 h-3.5" /> Collect payment
          </button>
        )}
        <ActionChip icon={Download} onClick={() => onDownloadPdf("digital")} testid="drawer-download">
          Download PDF
        </ActionChip>
        <ActionChip icon={Printer} onClick={() => onDownloadPdf("letterhead")} testid="drawer-print">
          Print
        </ActionChip>
        <ActionChip icon={Mail} onClick={onEmail} testid="drawer-email">Email</ActionChip>
        <ActionChip icon={MessageCircle} onClick={onWhatsapp} testid="drawer-whatsapp">WhatsApp</ActionChip>
      </div>

      <div className="px-6 py-5 space-y-6">
        {/* ================= Invoice Summary ================= */}
        <Section title="Invoice Summary">
          <SummaryGrid>
            <SummaryRow label="Patient" value={patient?.name || "—"}
                        sub={patient?.phone} />
            <SummaryRow label="Doctor"
                        value={doctor_names?.length ? doctor_names.join(", ") : "Unassigned"} />
            <SummaryRow label="Treatment"
                        value={
                          treatments?.length ? treatments.map((t) =>
                            `${t.procedure}${t.tooth ? ` (Tooth ${t.tooth})` : ""}`
                          ).join(", ")
                          : (invoice.items?.[0]?.name || "—")
                        } />
            <SummaryRow label="Invoice date" value={fmtDate(invoice.created_at)} />
            {invoice.due_date && (
              <SummaryRow label="Due date" value={fmtDate(invoice.due_date)} />
            )}
            {invoice.notes && (
              <SummaryRow label="Notes" value={invoice.notes} />
            )}
          </SummaryGrid>
        </Section>

        {/* ================= Financial Summary ================= */}
        <Section title="Financial Summary">
          <div className="rounded-2xl bg-white border border-[#EFEADC] p-4 space-y-1.5 text-sm">
            <Money label="Subtotal" v={invoice.subtotal} />
            {invoice.discount_amount > 0 && (
              <Money label={`Discount (${invoice.discount_type})`} v={-invoice.discount_amount} muted />
            )}
            {invoice.cgst_amount > 0 && <Money label={`CGST ${invoice.gst_rate / 2}%`} v={invoice.cgst_amount} muted />}
            {invoice.sgst_amount > 0 && <Money label={`SGST ${invoice.gst_rate / 2}%`} v={invoice.sgst_amount} muted />}
            {invoice.igst_amount > 0 && <Money label={`IGST ${invoice.gst_rate}%`} v={invoice.igst_amount} muted />}
            <div className="border-t border-[#F0EBE0] my-2" />
            <div className="flex items-baseline justify-between">
              <span className="text-ink font-medium">Grand total</span>
              <span className="font-display text-2xl tabular-nums">{INR(invoice.total)}</span>
            </div>
            <div className="flex items-baseline justify-between text-emerald-700">
              <span className="text-sm">Paid</span>
              <span className="font-mono text-sm">{INR(paid)}</span>
            </div>
            <div className={`flex items-baseline justify-between ${outstanding > 0.01 ? "text-clay" : "text-ink-mid"}`}>
              <span className="text-sm">Outstanding</span>
              <span className="font-mono text-sm">{INR(outstanding)}</span>
            </div>
          </div>
        </Section>

        {/* ================= Payment History ================= */}
        <Section title="Payment History">
          {payment_history.length === 0 ? (
            <div className="text-xs text-ink-soft py-4 text-center border border-dashed border-[#EFEADC] rounded-2xl">
              No payments recorded for this invoice yet.
            </div>
          ) : (
            <div className="rounded-2xl bg-white border border-[#EFEADC] divide-y divide-[#F5F1E5]" data-testid="drawer-payment-history">
              {payment_history.map((p, idx) => {
                const meta = METHOD_META[p.method] || METHOD_META.cash;
                const Icon = meta.icon;
                return (
                  <div key={`${p.id}-${idx}`} className="flex items-center gap-3 px-4 py-3">
                    <span className={`w-9 h-9 rounded-full grid place-items-center border ${meta.tone}`}>
                      <Icon className="w-4 h-4" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-ink font-medium">
                        {meta.label} · <span className="tabular-nums">{INR(p.amount)}</span>
                      </div>
                      <div className="text-[11px] text-ink-mid mt-0.5 truncate">
                        {fmtDT(p.ts)}
                        {p.ref ? ` · Ref ${p.ref}` : ""}
                        {p.collected_by ? ` · by ${p.collected_by}` : ""}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div className="flex items-baseline justify-between px-4 py-3 bg-linen/40 text-sm">
                <span className="text-ink-mid">Outstanding</span>
                <span className={`font-mono ${outstanding > 0.01 ? "text-clay font-medium" : "text-emerald-700"}`}>
                  {INR(outstanding)}
                </span>
              </div>
            </div>
          )}
        </Section>

        {/* ================= Timeline ================= */}
        <Section title="Timeline">
          <ol className="relative pl-5 space-y-3" data-testid="drawer-timeline">
            <span className="absolute left-1.5 top-1.5 bottom-1.5 w-px bg-[#EFEADC]" aria-hidden />
            {timeline.map((t, idx) => (
              <li key={idx} className="relative">
                <span className={`absolute -left-[13px] top-1 w-2.5 h-2.5 rounded-full border-2 border-white ${
                  t.kind === "payment" ? "bg-emerald-500"
                  : t.kind === "cancelled" ? "bg-red-500"
                  : t.kind === "emailed" || t.kind === "whatsapp" ? "bg-blue-500"
                  : "bg-pine"
                }`} />
                <div className="text-xs text-ink">{t.label}</div>
                <div className="text-[10px] text-ink-soft mt-0.5">{fmtDT(t.ts)}</div>
              </li>
            ))}
          </ol>
        </Section>

        {/* ================= Audit ================= */}
        <Section title="Audit">
          <SummaryGrid>
            <SummaryRow label="Created by"
                        value={audit.created_by || "—"}
                        sub={audit.created_by_role} />
            <SummaryRow label="Created at" value={fmtDT(audit.created_at)} />
            <SummaryRow label="Last updated" value={fmtDT(audit.last_updated_at)} />
          </SummaryGrid>
        </Section>
      </div>
    </>
  );
}


/* -------------------- primitives -------------------- */

function StatusBadge({ status }) {
  const map = {
    paid:      { label: "Paid",           cls: "bg-[#E8F0EB] text-[#2D6A4F] border-[#B8D6C2]" },
    partial:   { label: "Partial",        cls: "bg-[#FFF4E5] text-[#8A4B00] border-[#F2C494]" },
    pending:   { label: "Pending",        cls: "bg-[#FDF3ED] text-[#D06A42] border-[#F5C9B0]" },
    overdue:   { label: "Overdue",        cls: "bg-[#FCECEC] text-[#B94A48] border-[#F0B0AE]" },
    cancelled: { label: "Cancelled",      cls: "bg-[#EFEEEA] text-[#6E6D69] border-[#DDDBD3]" },
    refunded:  { label: "Refunded",       cls: "bg-[#EEF0FA] text-[#3C4A8A] border-[#C7CFEB]" },
  };
  const m = map[status] || map.pending;
  return (
    <span className={`shrink-0 text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border font-medium ${m.cls}`}
          data-testid={`drawer-status-${status}`}>
      {m.label}
    </span>
  );
}

function ActionChip({ icon: Icon, children, onClick, testid }) {
  return (
    <button
      type="button" onClick={onClick} data-testid={testid}
      className="text-[11px] px-3 py-1.5 rounded-full border border-[#E8EAE9] text-ink-mid bg-white hover:bg-linen inline-flex items-center gap-1.5"
    >
      <Icon className="w-3.5 h-3.5" /> {children}
    </button>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <div className="overline text-ink-soft mb-2">{title}</div>
      {children}
    </section>
  );
}

function SummaryGrid({ children }) {
  return (
    <div className="rounded-2xl bg-white border border-[#EFEADC] divide-y divide-[#F5F1E5]">
      {children}
    </div>
  );
}

function SummaryRow({ label, value, sub }) {
  return (
    <div className="flex items-baseline justify-between gap-4 px-4 py-2.5">
      <span className="text-[10px] uppercase tracking-widest text-ink-soft shrink-0">{label}</span>
      <div className="text-right min-w-0">
        <div className="text-sm text-ink truncate">{value || "—"}</div>
        {sub && <div className="text-[10px] text-ink-soft mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}

function Money({ label, v, muted }) {
  return (
    <div className={`flex items-baseline justify-between text-sm ${muted ? "text-ink-mid" : "text-ink"}`}>
      <span>{label}</span>
      <span className="font-mono tabular-nums">{INR(v)}</span>
    </div>
  );
}
